'use client';
import React, { ChangeEventHandler, MouseEventHandler } from 'react';
import { Flex3XS } from '@/components/Flex';
import Typography from '@/components/core/Typography/Typography';
import styled, { css } from 'styled-components';
import Icon from '@/components/core/Icon/Icon';

const CheckMark = styled(Icon)`
    background-color: ${({ theme }) => theme.colors.palette.white};
    transition: opacity 0.2s ease-in-out;
    opacity: 0;
`;

const NoSign = styled(Icon)`
    color: ${({ theme }) => theme.colors.palette.grey_600};
`;

export const CheckboxRoot = styled.div<{ $checked: boolean, $disabled: boolean }>`
    width: 14px;
    height: 14px;
    display: flex;
    justify-content: center;
    align-items: center;
  
    margin: 4px;
    border-radius: 4px;
    position: relative;
    border: 1px solid transparent;
    transition: box-shadow 0.2s ease-in-out, background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;
    box-sizing: content-box;

    ${({ $checked }) => $checked ? css`
        background-color: ${({ theme }) => theme.colors.primary.focused};
        border-color: ${({ theme }) => theme.colors.primary.focused};

        ${CheckMark} {
            opacity: 1;
        }
    ` : css`
        border-color: ${({ theme }) => theme.colors.palette.grey_600};
        background-color: ${({ theme }) => theme.colors.palette.white};
    `}

    ${({ $disabled, $checked }) => !$disabled && !$checked && css`
        &:hover {
            background-color: ${({ theme }) => theme.colors.palette.grey_300};
            box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.palette.grey_300} !important;
        }
    `}
    
     ${({ $disabled }) => !$disabled && css`
         &:hover {
             box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.primary.main};
         }
    `}
`;

export const StyledTypography = styled(Typography)`
    color: ${({ theme }) => theme.colors.palette.grey_800};
    cursor: pointer;
`;

const HiddenInput = styled.input`
    display: none;
`;

export const StyledFlex3XS = styled(Flex3XS)<{
    $disabled?: boolean
}>`
    align-items: center;
    cursor: pointer;
    
    > span {
        height: fit-content;
    }

    ${({ $disabled, theme }) => $disabled && css`
        opacity: ${theme.extras.opacity.disabled};
        cursor: default!important;
        & > * {
            cursor: default!important;
        }
    `}
`;

interface CheckboxProps {
    label?: React.ReactNode | string;
    inverted?: boolean;
    disabled?: boolean;
    checked?: boolean;
    intermediate?: boolean;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    className?: string;
    onClick?: MouseEventHandler;
    readOnly?: boolean;
    name?: string;
}

function Checkbox({
    label,
    inverted = false,
    disabled = false,
    intermediate = false,
    checked = false,

    onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        void event;
    },

    className = undefined,
    ...props
}: CheckboxProps) {
    return (
        <label className={className}>
            <StyledFlex3XS $inverted={inverted} $disabled={disabled}>
                {label && (typeof label === 'string'
                    ? <StyledTypography variant='uiType' size='sm' modifier='R'>
                        {label}
                      </StyledTypography>
                    : label)
                }

                <CheckboxRoot $disabled={disabled} $checked={checked || intermediate}>
                    <HiddenInput
                      type='checkbox'
                      disabled={disabled ? true : undefined}
                      checked={checked || intermediate}
                      onChange={onChange}
                      {...props}
                    />
                    {
                        disabled && !checked ? <NoSign name='nosign-light' size='sm' /> : (
                            intermediate ? <CheckMark name='minus-light' size='sm' /> : <CheckMark name='checkmark-light' size='sm' />
                        )
                    }
                </CheckboxRoot>
            </StyledFlex3XS>
        </label>
    );
}

export default Checkbox;
